/** Calculates absolute CSS positions and dimensions for time-slotted tasks, handling overlaps in daily views. */
import {
  CALENDAR_START_HOUR,
  DEFAULT_TASK_DURATION_MINS,
  TIME_SLOT_MINS,
} from '@/config/constants';

/**
 * Calculates absolute CSS positions and dimensions for a list of time-slotted tasks,
 * handling visual overlap by distributing concurrent tasks across columns.
 *
 * Algorithm (5 steps):
 *  1. Enrich each task with numeric `start` and `end` (minutes from midnight).
 *  2. Sort tasks by start time (ascending).
 *  3. Assign a `column` index to each task — the lowest column where no overlap exists.
 *  4. Compute `totalColumns` for each task based on the size of its overlap group.
 *  5. Generate `style` objects (`top`, `height`, `width`, `left`, `position`).
 *
 * @param {Array<{id: string, time?: string, duration?: number, isNew?: boolean}>} tasks
 *   Tasks to position. `time` must be in `'HH:mm'` format; `duration` is in minutes
 *   (defaults to DEFAULT_TASK_DURATION_MINS if absent). Tasks with `isNew: true` get single-slot height.
 * @param {number} fifteenMinuteSlotHeight - Pixel height of one 15-minute time slot.
 * @param {number} [startHour] - Hour the rendered grid actually starts at (defaults to
 *   CALENDAR_START_HOUR). Must match the `startHour` passed to `useTimeSlots` for the
 *   same grid, or tasks are positioned relative to the wrong row.
 * @returns {Array<Object>} A new array where every task is augmented with:
 *   - `start` {number}  — start time in minutes from midnight
 *   - `end`   {number}  — end time in minutes from midnight
 *   - `column` {number} — zero-based column index within the overlap group
 *   - `totalColumns` {number} — total columns in the overlap group
 *   - `style` {Object} — CSS style object (`top`, `height`, `width`, `left`, `position`)
 *   - `isPlaced` {true}
 */
export const calculateTaskPositions = (
  tasks,
  fifteenMinuteSlotHeight,
  startHour = CALENDAR_START_HOUR,
) => {
  /**
   * Converts an `'HH:mm'` time string to total minutes from midnight.
   * Returns `0` for falsy input to avoid crashes on tasks without a time.
   *
   * @param {string|undefined} time - Time string in `'HH:mm'` format.
   * @returns {number} Minutes from midnight (e.g. `'08:15'` → `495`).
   */
  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  /**
   * Converts an `'HH:mm'` time string to the pixel offset from the top of the
   * scrollable calendar area. Assumes the calendar grid starts at **CALENDAR_START_HOUR**.
   * Returns `0` for falsy input.
   *
   * @param {string|undefined} time - Time string in `'HH:mm'` format.
   * @returns {number} Pixel offset relative to the CALENDAR_START_HOUR row
   *   (e.g. `'08:15'` with a 20px slot height → `20`).
   */
  const slotToTop = (time) => {
    if (!time) return 0;
    const totalMinutes = timeToMinutes(time);
    const startMinutes = startHour * 60;
    return (
      ((totalMinutes - startMinutes) / TIME_SLOT_MINS) * fifteenMinuteSlotHeight
    );
  };

  // 1. Add start and end times in minutes to each task for easier comparison
  const tasksWithTime = tasks.map((task) => ({
    ...task,
    start: timeToMinutes(task.time),
    end:
      timeToMinutes(task.time) + (task.duration || DEFAULT_TASK_DURATION_MINS),
  }));

  // 2. Sort tasks by their start time
  const sortedTasks = [...tasksWithTime].sort((a, b) => a.start - b.start);

  // 3. Determine the column for each task to avoid visual overlap
  const tasksWithColumns = [];
  for (let i = 0; i < sortedTasks.length; i++) {
    const currentTask = sortedTasks[i];
    let column = 0;
    // Find the first column where the task doesn't overlap with previously placed tasks
    while (true) {
      const overlappingTaskInColumn = tasksWithColumns.find(
        (t) =>
          t.column === column &&
          currentTask.start < t.end &&
          currentTask.end > t.start,
      );
      if (overlappingTaskInColumn) {
        column++; // This column is taken, try the next one
      } else {
        break; // This column is free
      }
    }
    tasksWithColumns.push({ ...currentTask, column });
  }

  // 4. Determine the total number of columns needed for each task's overlapping group
  const finalTasks = tasksWithColumns.map((task) => {
    // Find all tasks that overlap with the current one
    const overlappingGroup = tasksWithColumns.filter(
      (t) => task.start < t.end && task.end > t.start,
    );
    // The total number of columns is the highest column index in the group + 1
    const totalColumns = Math.max(...overlappingGroup.map((t) => t.column)) + 1;

    return { ...task, totalColumns };
  });

  // 5. Generate the final list of tasks with their calculated CSS styles
  const visuallyPlacedTasks = finalTasks.map((task) => {
    const top = slotToTop(task.time);

    // Calculate height based on duration. Subtract 1px to leave the slot border visible.
    const height = task.isNew
      ? fifteenMinuteSlotHeight - 1
      : ((task.duration || DEFAULT_TASK_DURATION_MINS) / TIME_SLOT_MINS) *
          fifteenMinuteSlotHeight -
        1;

    const rawWidth = 100 / task.totalColumns;
    const width = `${Math.round(rawWidth * 100) / 100}%`;
    const left = `${Math.round(task.column * rawWidth * 100) / 100}%`;

    return {
      ...task,
      style: {
        top: `${top}px`,
        height: `${height}px`,
        width,
        left,
        position: 'absolute', // Ensure tasks are positioned absolutely
      },
      isPlaced: true,
    };
  });

  return visuallyPlacedTasks;
};
